import { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Sheet } from '../components/ui/Sheet';
import { Badge } from '../components/ui/Badge';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { PasswordChange } from '../components/PasswordChange';
import { AdminSettings } from './AdminSettings';
import { Plus, Trash2, Pause, Play, Pencil, KeyRound, FileText } from 'lucide-react';
import { ResetPasswordSheet } from '../components/ResetPasswordSheet';
import { InvoiceSheet } from '../components/InvoiceSheet';
import { BulkInvoiceSheet } from '../components/BulkInvoiceSheet';
import { SchoolHeadFilters } from '../components/SchoolHeadFilters';
import { AdminRenewalsPanel } from '../components/admin/AdminRenewalsPanel';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { PRICE_PER_CLASS_YEAR } from '../constants/invoiceIssuer';
import {
  defaultAcademicYearEndDate,
  defaultTrialEndDate,
  formatSubscriptionDate,
  isSubscriptionExpired,
  daysUntilExpiry,
} from '../utils/academicYear';
import {
  applySchoolHeadFilters,
  getUniqueExpiryDates,
} from '../utils/adminSchoolFilters';

function isSchoolHeadAccount(head) {
  return head.account_role !== 'admin' && head.username !== 'admin';
}

function subscriptionBadge(head, labels) {
  const exp = head.subscription_expires_at;
  if (!exp) return <Badge variant="default">—</Badge>;
  if (isSubscriptionExpired(exp)) {
    return <Badge variant="warning">{labels.subscriptionExpired}</Badge>;
  }
  const days = daysUntilExpiry(exp);
  if (days !== null && days <= 14) {
    return <Badge variant="warning">{labels.subscriptionExpiringSoon}</Badge>;
  }
  return <Badge variant="success">{formatSubscriptionDate(exp)}</Badge>;
}

export function AdminDashboard({ activeTab }) {
  const app = useApp();
  const {
    labels,
    token,
    schoolHeads,
    fetchSchoolHeads,
    isLoading,
    newHead,
    setNewHead,
    addSchoolHead,
    deleteSchoolHead,
    pauseHead,
    continueHead,
    setDivisionsForHead,
    setSubscriptionForHead,
    testerSignups,
    fetchTesterSignups,
    exportTesterSignups,
    copyTesterEmails,
    resetHeadPassword,
    invoiceIssuer,
    fetchInvoiceIssuer,
    saveInvoiceIssuer,
    toastSuccess,
  } = app;

  const [sheetOpen, setSheetOpen] = useState(false);
  const [resetPasswordHead, setResetPasswordHead] = useState(null);
  const [invoiceHead, setInvoiceHead] = useState(null);
  const [selectedHeadIds, setSelectedHeadIds] = useState(() => new Set());
  const [bulkInvoiceHeads, setBulkInvoiceHeads] = useState(null);
  const [editHead, setEditHead] = useState(null);
  const [editDivisions, setEditDivisions] = useState('');
  const [editSubscriptionExpires, setEditSubscriptionExpires] = useState('');
  const [search, setSearch] = useState('');
  const [testerSearch, setTesterSearch] = useState('');
  const [validExact, setValidExact] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [validPreset, setValidPreset] = useState('all');
  const [divisionsSort, setDivisionsSort] = useState('none');
  const [schoolsView, setSchoolsView] = useState('all');
  const [markingPaidId, setMarkingPaidId] = useState(null);

  const openAddSheet = () => {
    setNewHead((prev) => ({
      ...prev,
      subscriptionExpiresAt: prev.subscriptionExpiresAt || defaultTrialEndDate(),
    }));
    setSheetOpen(true);
  };

  const estimatedFee = (divisions) => {
    const n = Math.max(0, parseInt(divisions, 10) || 0);
    return n * PRICE_PER_CLASS_YEAR;
  };

  const openEditHead = (head) => {
    setEditHead(head);
    setEditDivisions(String(head.divisions ?? 0));
    setEditSubscriptionExpires(
      head.subscription_expires_at
        ? String(head.subscription_expires_at).slice(0, 10)
        : defaultTrialEndDate()
    );
  };

  const saveEditHead = async () => {
    if (!editHead) return;
    const okDiv = await setDivisionsForHead(editHead.username, editDivisions);
    const okSub = await setSubscriptionForHead(editHead.id, editSubscriptionExpires);
    if (okDiv !== false && okSub) setEditHead(null);
  };

  const filteredTesterSignups = (testerSignups || []).filter(
    (row) => !testerSearch || row.email?.toLowerCase().includes(testerSearch.toLowerCase())
  );

  const testerColumns = [
    { key: 'email', label: labels.testerEmail, render: (r) => r.email },
    { key: 'consent_accepted', label: labels.consent, render: (r) => (r.consent_accepted ? labels.yes : labels.no) },
    { key: 'allow_future_apps', label: labels.futureApps, render: (r) => (r.allow_future_apps ? labels.yes : labels.no) },
    { key: 'created_at', label: labels.submittedAt, render: (r) => new Date(r.created_at).toLocaleString() },
  ];

  const headsOnly = useMemo(
    () => (schoolHeads || []).filter(isSchoolHeadAccount),
    [schoolHeads]
  );
  const uniqueExpiryDates = useMemo(() => getUniqueExpiryDates(headsOnly), [headsOnly]);
  const filtered = useMemo(
    () =>
      applySchoolHeadFilters(headsOnly, {
        search,
        validExact,
        validFrom,
        validTo,
        validPreset,
        divisionsSort,
      }),
    [headsOnly, search, validExact, validFrom, validTo, validPreset, divisionsSort]
  );

  const clearSchoolFilters = () => {
    setValidExact('');
    setValidFrom('');
    setValidTo('');
    setValidPreset('all');
    setDivisionsSort('none');
  };

  const handleMarkPaid = async (head) => {
    setMarkingPaidId(head.id);
    const ok = await setSubscriptionForHead(head.id, defaultAcademicYearEndDate());
    setMarkingPaidId(null);
    if (ok) fetchSchoolHeads();
  };

  if (activeTab === 'account') {
    return <PasswordChange token={token} labels={labels} />;
  }

  if (activeTab === 'settings') {
    return <AdminSettings />;
  }

  if (activeTab === 'testers') {
    return (
      <div className="space-y-4">
        <PageHeader
          title={labels.testerSignupsTitle}
          actions={
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fetchTesterSignups(testerSearch)}
                isLoading={isLoading.testerSignups}
              >
                {labels.refresh}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => copyTesterEmails()}>
                {labels.copyEmails}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => exportTesterSignups('csv')}>
                {labels.downloadCSV}
              </Button>
            </>
          }
        />

        <Input
          placeholder={labels.search}
          value={testerSearch}
          onChange={(e) => {
            setTesterSearch(e.target.value);
            fetchTesterSignups(e.target.value);
          }}
          className="max-w-md"
        />

        <Card>
          <CardBody className="space-y-3">
            <p className="text-sm text-muted">{labels.testerSignupsSubtitle}</p>
            {isLoading.testerSignups ? (
              <SkeletonCard />
            ) : filteredTesterSignups.length === 0 ? (
              <EmptyState title={labels.noData} message={labels.testerSignupsSubtitle} />
            ) : (
              <DataTable
                columns={testerColumns}
                data={filteredTesterSignups}
                emptyMessage={labels.noData}
                renderMobileCard={(row) => (
                  <Card>
                    <CardBody className="space-y-2">
                      <p className="font-medium break-all">{row.email}</p>
                      <p className="text-sm text-muted">
                        {labels.consent}: {row.consent_accepted ? labels.yes : labels.no}
                      </p>
                      <p className="text-sm text-muted">
                        {labels.futureApps}: {row.allow_future_apps ? labels.yes : labels.no}
                      </p>
                      <p className="text-xs text-muted">{new Date(row.created_at).toLocaleString()}</p>
                    </CardBody>
                  </Card>
                )}
              />
            )}
          </CardBody>
        </Card>
      </div>
    );
  }

  const toggleHeadSelected = (id) => {
    setSelectedHeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((h) => selectedHeadIds.has(h.id));

  const toggleSelectAllFiltered = () => {
    setSelectedHeadIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((h) => next.delete(h.id));
      } else {
        filtered.forEach((h) => next.add(h.id));
      }
      return next;
    });
  };

  const openBulkInvoiceSheet = () => {
    const selected = filtered.filter((h) => selectedHeadIds.has(h.id));
    if (selected.length === 0) return;
    setBulkInvoiceHeads(selected);
  };

  const handleBulkInvoicesDone = ({ generated, skipped }) => {
    toastSuccess(
      skipped > 0
        ? `${labels.toastInvoicesZipDone} (${skipped} ${labels.toastInvoiceSkippedNoDivisions})`
        : labels.toastInvoicesZipDone
    );
    if (generated > 0) setSelectedHeadIds(new Set());
  };

  const renderActions = (head) => (
    <div className="flex items-center justify-end gap-1">
      <Button
        variant="ghost"
        size="sm"
        aria-label={labels.resetPassword}
        onClick={() => setResetPasswordHead(head)}
      >
        <KeyRound className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        aria-label={labels.generateInvoice}
        onClick={() => setInvoiceHead(head)}
      >
        <FileText className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        aria-label={labels.editHeadSettings}
        onClick={() => openEditHead(head)}
      >
        <Pencil className="h-4 w-4" />
      </Button>
      {head.paused ? (
        <Button variant="ghost" size="sm" aria-label={labels.resume} onClick={() => continueHead(head.id)}>
          <Play className="h-4 w-4" />
        </Button>
      ) : (
        <Button variant="ghost" size="sm" aria-label={labels.pause} onClick={() => pauseHead(head.id)}>
          <Pause className="h-4 w-4" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="text-danger hover:bg-danger-bg"
        aria-label={labels.delete}
        onClick={() => deleteSchoolHead(head.id, head.name)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );

  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={allFilteredSelected}
          onChange={toggleSelectAllFiltered}
          aria-label={labels.invoiceSelectSchools}
          className="h-4 w-4 rounded border-border"
        />
      ),
      render: (h) => (
        <input
          type="checkbox"
          checked={selectedHeadIds.has(h.id)}
          onChange={() => toggleHeadSelected(h.id)}
          aria-label={`${labels.invoiceSelectSchools}: ${h.name}`}
          className="h-4 w-4 rounded border-border"
        />
      ),
    },
    {
      key: 'name',
      label: labels.name,
      render: (h) => (
        <button
          type="button"
          onClick={() => setInvoiceHead(h)}
          className="text-left hover:text-primary"
        >
          <p className="font-medium text-text">{h.name}</p>
          <p className="text-xs text-muted">@{h.username}</p>
        </button>
      ),
    },
    {
      key: 'divisions',
      label: labels.numDivisionsLabel,
      render: (h) => (
        <span className="tabular-nums font-medium">{h.divisions ?? 0}</span>
      ),
    },
    {
      key: 'subscription',
      label: labels.subscriptionValidUntil,
      render: (h) => subscriptionBadge(h, labels),
    },
    {
      key: 'status',
      label: labels.status,
      render: (h) => (
        <Badge variant={h.paused ? 'warning' : 'success'}>
          {h.paused ? labels.paused : labels.active}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: labels.actions,
      render: (h) => renderActions(h),
    },
  ];

  return (
    <div className="space-y-4">
      <SegmentedControl
        options={[
          { value: 'all', label: labels.schoolsViewAll },
          { value: 'renewals', label: labels.renewalsTitle },
        ]}
        value={schoolsView}
        onChange={setSchoolsView}
      />

      {schoolsView === 'renewals' ? (
        <AdminRenewalsPanel
          labels={labels}
          heads={headsOnly}
          isLoading={isLoading.heads}
          onRefresh={fetchSchoolHeads}
          onMarkPaid={handleMarkPaid}
          markingPaidId={markingPaidId}
        />
      ) : (
        <>
      <PageHeader
        title={labels.schoolHeads}
        actions={
          <>
            {selectedHeadIds.size > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={openBulkInvoiceSheet}
              >
                {labels.bulkGenerateInvoices} ({selectedHeadIds.size})
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={() => fetchSchoolHeads()} isLoading={isLoading.heads}>
              {labels.refresh}
            </Button>
            <Button size="sm" onClick={openAddSheet}>
              <Plus className="h-4 w-4" />
              {labels.addSchoolHead}
            </Button>
          </>
        }
      />
      <SchoolHeadFilters
        labels={labels}
        search={search}
        onSearchChange={setSearch}
        uniqueExpiryDates={uniqueExpiryDates}
        validExact={validExact}
        setValidExact={setValidExact}
        validFrom={validFrom}
        setValidFrom={setValidFrom}
        validTo={validTo}
        setValidTo={setValidTo}
        validPreset={validPreset}
        setValidPreset={setValidPreset}
        divisionsSort={divisionsSort}
        setDivisionsSort={setDivisionsSort}
        shownCount={filtered.length}
        totalCount={headsOnly.length}
        onClear={clearSchoolFilters}
      />
      {isLoading.heads ? (
        <SkeletonCard />
      ) : filtered.length === 0 ? (
        <EmptyState title={labels.noData} actionLabel={labels.addSchoolHead} onAction={openAddSheet} />
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          emptyMessage={labels.noData}
          renderMobileCard={(head) => (
            <Card>
              <CardBody className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedHeadIds.has(head.id)}
                      onChange={() => toggleHeadSelected(head.id)}
                      className="mt-1 h-4 w-4 shrink-0 rounded border-border"
                      aria-label={`${labels.invoiceSelectSchools}: ${head.name}`}
                    />
                    <button type="button" onClick={() => setInvoiceHead(head)} className="min-w-0 text-left">
                      <p className="font-semibold text-text truncate">{head.name}</p>
                      <p className="text-sm text-muted">@{head.username}</p>
                    </button>
                  </div>
                  <Badge variant={head.paused ? 'warning' : 'success'}>
                    {head.paused ? labels.paused : labels.active}
                  </Badge>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-surface px-3 py-2">
                  <span className="text-sm text-muted">{labels.numDivisionsLabel}</span>
                  <span className="text-lg font-semibold tabular-nums text-text">{head.divisions ?? 0}</span>
                </div>
                <div className="flex items-center justify-between rounded-lg bg-surface px-3 py-2">
                  <span className="text-sm text-muted">{labels.subscriptionValidUntil}</span>
                  {subscriptionBadge(head, labels)}
                </div>
                <div className="flex justify-end border-t border-border pt-3">
                  {renderActions(head)}
                </div>
              </CardBody>
            </Card>
          )}
        />
      )}

        </>
      )}

      <Sheet open={sheetOpen} onClose={() => setSheetOpen(false)} title={labels.addSchoolHead}>
        <div className="space-y-4">
          <Input label={labels.name} value={newHead.name} onChange={(e) => setNewHead({ ...newHead, name: e.target.value })} />
          <Input label={labels.username} value={newHead.username} onChange={(e) => setNewHead({ ...newHead, username: e.target.value })} />
          {newHead.username.trim() && (
            <p className="-mt-2 text-xs text-muted">
              {labels.schoolCodeHint.replaceAll('{code}', newHead.username.trim())}
            </p>
          )}
          <Input label={labels.password} type="password" value={newHead.password} onChange={(e) => setNewHead({ ...newHead, password: e.target.value })} />
          <p className="text-xs text-muted">{labels.pricingModelHint}</p>
          <Input
            label={labels.numDivisionsLabel}
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={newHead.divisions}
            onChange={(e) => setNewHead({ ...newHead, divisions: e.target.value })}
          />
          {newHead.divisions !== '' && (
            <p className="-mt-2 text-sm font-medium text-primary">
              {labels.estimatedYearlyFee}: ₹{estimatedFee(newHead.divisions)}/year
            </p>
          )}
          <Input
            label={labels.subscriptionValidUntil}
            type="date"
            value={newHead.subscriptionExpiresAt || defaultTrialEndDate()}
            onChange={(e) => setNewHead({ ...newHead, subscriptionExpiresAt: e.target.value })}
          />
          <p className="-mt-2 text-xs text-muted">{labels.subscriptionExpiresHint}</p>
          <Button
            className="w-full"
            onClick={async () => {
              await addSchoolHead();
              setSheetOpen(false);
            }}
            isLoading={isLoading.addHead}
          >
            {labels.addHead}
          </Button>
        </div>
      </Sheet>

      <Sheet
        open={!!editHead}
        onClose={() => setEditHead(null)}
        title={labels.editHeadSettings}
      >
        {editHead && (
          <div className="space-y-4">
            <p className="text-sm text-muted">
              {editHead.name} <span className="text-muted">(@{editHead.username})</span>
            </p>
            <p className="text-xs text-muted">{labels.pricingModelHint}</p>
            <Input
              label={labels.numDivisionsLabel}
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={editDivisions}
              onChange={(e) => setEditDivisions(e.target.value)}
            />
            <p className="text-sm font-medium text-primary">
              {labels.estimatedYearlyFee}: ₹{estimatedFee(editDivisions)}/year
            </p>
            <Input
              label={labels.subscriptionValidUntil}
              type="date"
              value={editSubscriptionExpires}
              onChange={(e) => setEditSubscriptionExpires(e.target.value)}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setEditSubscriptionExpires(defaultTrialEndDate())}
              >
                {labels.setTrialMonth}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setEditSubscriptionExpires(defaultAcademicYearEndDate())}
              >
                {labels.setPaidYear}
              </Button>
            </div>
            <p className="-mt-2 text-xs text-muted">{labels.subscriptionExpiresHint}</p>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() => {
                setInvoiceHead(editHead);
                setEditHead(null);
              }}
            >
              <FileText className="h-4 w-4" />
              {labels.generateInvoice}
            </Button>
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={() => setEditHead(null)}>
                {labels.cancel}
              </Button>
              <Button
                className="flex-1"
                onClick={saveEditHead}
                isLoading={isLoading.setDivisions || isLoading.setSubscription}
              >
                {labels.save}
              </Button>
            </div>
          </div>
        )}
      </Sheet>

      <InvoiceSheet
        open={!!invoiceHead}
        onClose={() => setInvoiceHead(null)}
        head={invoiceHead}
        labels={labels}
        savedIssuer={invoiceIssuer}
        fetchInvoiceIssuer={fetchInvoiceIssuer}
        saveInvoiceIssuer={saveInvoiceIssuer}
      />

      <BulkInvoiceSheet
        open={!!bulkInvoiceHeads?.length}
        onClose={() => setBulkInvoiceHeads(null)}
        heads={bulkInvoiceHeads}
        labels={labels}
        savedIssuer={invoiceIssuer}
        fetchInvoiceIssuer={fetchInvoiceIssuer}
        onDone={handleBulkInvoicesDone}
      />

      <ResetPasswordSheet
        open={!!resetPasswordHead}
        onClose={() => setResetPasswordHead(null)}
        title={labels.resetPasswordTitleHead}
        subtitle={
          resetPasswordHead ? `${resetPasswordHead.name} (@${resetPasswordHead.username})` : ''
        }
        labels={labels}
        isLoading={isLoading.resetHeadPassword}
        onSubmit={(newPassword) =>
          resetPasswordHead ? resetHeadPassword(resetPasswordHead.id, newPassword) : Promise.resolve(false)
        }
      />
    </div>
  );
}
